<template>
  <!-- The client's first retainer invoice on 369.dev (workspace-public-website
       components/Invoices/InvoicePayPanel.vue, locales/invoice/en.json): "Retainer terms"
       (Included, Price, Extra hours, Unused hours, Your hours), the consent box "Accept the
       retainer terms and pay future invoices automatically.", "Without the box ticked, you pay
       this invoice only.", "Pay {amount} and start the retainer", then the return screen
       ("Autopay is on", "Next charge", "Manage autopay"). The figures are sample data. Once in
       view the box ticks, the button changes, and the confirmation appears under it (about 4 s, once).
       Resting styles are the final frame. -->
  <div ref="rootEl" class="src" :class="{ 'is-run': running || armed, 'is-armed': armed }">
    <div class="src-window">
      <div class="src-pay">
        <p class="src-kicker" translate="no">Invoice INV-1051 · Northwind Studio</p>
        <strong class="src-amount" translate="no">$4,500.00</strong>
        <section class="src-terms">
          <b>Retainer terms</b>
          <dl>
            <div><dt>Included</dt><dd translate="no">30 h each month</dd></div>
            <div><dt>Price</dt><dd translate="no">$4,500.00</dd></div>
            <div><dt>Extra hours</dt><dd translate="no">$150.00/h, billed on the next invoice</dd></div>
            <div><dt>Unused hours</dt><dd translate="no">Roll over to the next month, up to 10 h</dd></div>
            <div><dt>Your hours</dt><dd>Live in your client portal, with a statement each month</dd></div>
          </dl>
        </section>
        <p class="src-consent">
          <i class="src-box"><svg viewBox="0 0 24 24"><path :d="check" /></svg></i>
          <span>Accept the retainer terms and pay future invoices automatically.</span>
        </p>
        <p class="src-note">Without the box ticked, you pay this invoice only.</p>
        <span class="src-btn"><span class="src-btn-a" translate="no">Pay $4,500.00</span><span class="src-btn-b" translate="no">Pay $4,500.00 and start the retainer</span></span>
      </div>
      <div class="src-done">
        <div class="src-done-top">
          <span class="src-mark"><svg viewBox="0 0 24 24"><path :d="check" /></svg></span>
          <b>Autopay is on</b>
          <span class="src-link">Manage autopay</span>
        </div>
        <dl class="src-rows">
          <div><dt>Next charge</dt><dd translate="no">$4,500.00 · Dec 1</dd></div>
        </dl>
      </div>
    </div>
  </div>
</template>

<script setup>
const rootEl = ref(null);
const { running, armed } = useMotionInView(rootEl, { threshold: 0.4 });
const check = "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z";
</script>

<style scoped>
.src { display: grid; place-items: center; }
.src-window {
  position: relative;
  width: 100%;
  max-width: 400px;
  overflow: hidden;
  border: 1px solid rgb(255 255 255 / 0.14);
  border-radius: 10px;
  background: #1c1c1c;
  color: #ececec;
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.45;
  box-shadow: 0 24px 60px -20px rgb(0 0 0 / 0.8);
}
.src-pay, .src-done { display: grid; align-content: start; padding: 18px; }
.src-kicker { color: #a8a8a8; font-size: 11.5px; }
.src-amount { font-size: 28px; font-weight: 700; line-height: 1.2; font-variant-numeric: tabular-nums; }
.src-terms { margin-top: 12px; border: 1px solid rgb(255 255 255 / 0.12); border-radius: 8px; background: #242424; padding: 10px 12px; }
.src-terms > b { font-size: 12.5px; font-weight: 600; }
.src-terms dl { display: grid; gap: 5px; margin: 8px 0 0; }
.src-terms div { display: grid; grid-template-columns: 92px 1fr; gap: 8px; font-size: 12px; }
.src-terms dt { color: #a8a8a8; }
.src-terms dd { margin: 0; }
.src-consent { display: flex; align-items: flex-start; gap: 9px; margin-top: 14px; font-size: 12.5px; font-weight: 500; }
.src-box { display: grid; flex-shrink: 0; place-items: center; width: 18px; height: 18px; margin-top: 1px; border: 1.5px solid #ececec; border-radius: 4px; background: #ececec; color: #171717; }
.src-box svg { width: 13px; height: 13px; fill: currentColor; }
.src-note { margin-top: 6px; color: #9a9a9a; font-size: 11.5px; }
.src-btn { display: grid; margin-top: 12px; border-radius: 8px; background: #ececec; color: #171717; font-size: 13px; font-weight: 600; text-align: center; }
.src-btn > span { grid-area: 1 / 1; padding: 10px 8px; }
.src-btn-a { opacity: 0; }

.src-done { gap: 8px; border-top: 1px solid rgb(255 255 255 / 0.14); background: #232323; padding: 14px 18px 16px; }
.src-done-top { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 9px; }
.src-done-top b { flex: 1; font-size: 14.5px; font-weight: 600; white-space: nowrap; }
.src-mark { display: grid; place-items: center; width: 28px; height: 28px; border-radius: 50%; background: rgb(63 178 110 / 0.18); color: #6ed39a; }
.src-mark svg { width: 17px; height: 17px; fill: currentColor; }
.src-rows { margin: 0; border: 1px solid rgb(255 255 255 / 0.12); border-radius: 8px; background: #2b2b2b; }
.src-rows div { display: flex; justify-content: space-between; padding: 8px 12px; font-size: 12.5px; }
.src-rows dt { color: #a8a8a8; }
.src-rows dd { margin: 0; font-weight: 600; font-variant-numeric: tabular-nums; }
.src-link { white-space: nowrap; color: #93aef9; font-size: 12.5px; font-weight: 600; }

/* Resting frame: autopay on, the confirmation under the button. When it plays: unticked, the box ticks, the button changes, then the confirmation appears. */
.is-run .src-box { animation: src-tick 0.3s ease-out 1.1s both; }
.is-run .src-box svg { animation: src-fade 0.2s ease-out 1.2s both; }
.is-run .src-btn-a { animation: src-out 0.25s ease-in 1.3s both; }
.is-run .src-btn-b { animation: src-fade 0.3s ease-out 1.4s both; }
.is-run .src-btn { animation: src-press 0.25s ease-in-out 2.7s both; }
.is-run .src-done { animation: src-rise 0.55s cubic-bezier(0.33, 1, 0.68, 1) 3.2s both; }
.is-armed * { animation-play-state: paused !important; }
@keyframes src-tick { from { background: transparent; } to { background: #ececec; } }
@keyframes src-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes src-out { from { opacity: 1; } to { opacity: 0; } }
@keyframes src-press { 50% { transform: scale(0.97); } }
@keyframes src-rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@media (max-width: 639px) {
  .src-pay, .src-done { padding: 14px; }
  .src-terms div { grid-template-columns: 1fr; gap: 0; }
  .src-terms div:nth-child(2), .src-terms div:nth-child(5), .src-note { display: none; }
  .src-btn { font-size: 12.5px; }
  .src-done-top .src-link { flex-basis: 100%; padding-left: 37px; }
}
@media (prefers-reduced-motion: reduce) { .src * { animation: none !important; } }
</style>
